import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'chart.js/auto';
import Card from '../Card/Card';
import styles from './NoteEvolutionChart.module.css';

/**
 * NoteEvolutionChart — courbe Chart.js de la note Google sur N mois.
 *
 * Props :
 *  - title  : "Évolution note Google"
 *  - labels : ['Nov','Déc',…]      (depuis /api/dashboard/note-history)
 *  - data   : [4.2, 4.3, …]        (idem)
 *  - current: "4,7"
 *  - delta  : "↑ +0,5 depuis nov."
 *  - loading: bool — placeholder pendant le fetch
 *
 * IDs préservés :
 *  - #noteChart   (canvas — utilisé par le backend pour update direct)
 *
 * ⚠️ Hotfix 04/05/2026 — Les mocks 4.2→4.7 (DEFAULT_LABELS / DEFAULT_DATA)
 * ont été retirés. Si labels/data ne sont pas fournis ou trop courts (< 2
 * points), on affiche un empty state plutôt que de fausses données.
 */
export default function NoteEvolutionChart({
  title,
  labels,
  data,
  current = '—',
  delta = '',
  loading = false,
}) {
  const { t } = useTranslation('dashboard');
  const safeTitle = title || t('note_evolution.default_title');
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  // Si pas de data utilisable (chargement, < 2 points, données invalides),
  // on bypass Chart.js — il ne s'initialise pas sur un canvas absent.
  const hasData =
    Array.isArray(labels) && Array.isArray(data) &&
    labels.length >= 2 && data.length === labels.length &&
    data.every((v) => typeof v === 'number' && Number.isFinite(v));

  useEffect(() => {
    if (!hasData) return undefined;
    if (!Chart || !canvasRef.current) return undefined;
    const ctx = canvasRef.current.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 0, 200);
    g.addColorStop(0, 'rgba(245, 158, 11, 0.30)');
    g.addColorStop(1, 'rgba(245, 158, 11, 0)');

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data,
          borderColor: '#F59E0B',
          backgroundColor: g,
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#F59E0B',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 8,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#18160F',
            borderRadius: 8,
            padding: 10,
            titleFont: { family: 'Inter' },
            bodyFont:  { family: 'Inter' },
          },
        },
        scales: {
          y: {
            min: 4.0, max: 5.0,
            ticks: { color: '#ABA99F', font: { size: 10 } },
            grid:  { color: '#EDEAE3' },
          },
          x: {
            ticks: { color: '#ABA99F', font: { size: 10 } },
            grid:  { display: false },
          },
        },
        animation: { duration: 1500, easing: 'easeOutCubic' },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [labels, data, hasData]);

  return (
    <Card span={6} id="note-chart-card">
      <div className={styles.head}>
        <h2 className={styles.title}>
          <span className={styles.icon}>📈</span> {safeTitle}
        </h2>
        <div className={styles.right}>
          <span className={styles.big}>{current}</span>
          {delta && <span className={styles.delta}>{delta}</span>}
        </div>
      </div>
      <div className={styles.wrap}>
        {hasData ? (
          <canvas id="noteChart" ref={canvasRef} />
        ) : (
          <div className={styles.empty}>
            <span className={styles.emptyEmoji}>{loading ? t('note_evolution.empty_loading_emoji') : t('note_evolution.empty_emoji')}</span>
            <span className={styles.emptyTitle}>
              {loading ? t('note_evolution.empty_loading_title') : t('note_evolution.empty_title')}
            </span>
            {!loading && (
              <span className={styles.emptyHint}>
                {t('note_evolution.empty_hint')}
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
